import React, { useEffect, useMemo, useState } from 'react';
import theme from '../../theme';
import { previewAccountInstruments, confirmAccountInstruments } from '../../services/api';

const key = (i) => `${i.symbol}|${i.sectype || ''}`;
const ref = (i) => ({ symbol: i.symbol, sectype: i.sectype || '' });

export default function AccountInstrumentsModal({
  library, accounts, sourceAccount, instruments, onClose, onChanged,
}) {
  const accountNames = useMemo(() => [...new Set([
    ...accounts.filter((a) => a.broker === 'ib').map((a) => a.account), library.monitor_account,
  ].filter(Boolean))], [accounts, library.monitor_account]);
  const [source, setSource] = useState(sourceAccount || instruments?.[0]?.accounts?.[0] || accountNames[0]);
  const [target, setTarget] = useState(library.monitor_account === source
    ? accountNames.find((a) => a !== source) || '' : library.monitor_account);
  const [operation, setOperation] = useState('move');
  const candidates = useMemo(() => (instruments || library.instruments)
    .filter((i) => i.broker === 'ib' && i.accounts.includes(source)),
  [instruments, library.instruments, source]);
  const [selected, setSelected] = useState(() => candidates.map(key));
  const [review, setReview] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [acknowledged, setAcknowledged] = useState(false);

  useEffect(() => { setSelected(candidates.map(key)); setReview(null); }, [candidates]);
  useEffect(() => {
    const handler = (event) => { if (event.key === 'Escape' && !busy) onClose(); };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [busy, onClose]);

  const changeSource = (value) => {
    setSource(value);
    if (target === value) setTarget(accountNames.find((a) => a !== value) || '');
    setReview(null); setError('');
  };
  const request = {
    broker: 'ib', source_account: source, target_account: operation === 'move' ? target : null,
    operation, instruments: candidates.filter((i) => selected.includes(key(i))).map(ref),
  };
  const loadPreview = async () => {
    setBusy(true); setError(''); setAcknowledged(false);
    try {
      const result = await previewAccountInstruments(request);
      if (!result || result.error) setError(result?.error || 'Could not review instruments');
      else setReview({ plan: result, request });
    } catch (e) {
      setError('Could not review instruments. Try again.');
    } finally { setBusy(false); }
  };
  const confirm = async () => {
    setBusy(true); setError('');
    try {
      const result = await confirmAccountInstruments({ ...review.request, preview_token: review.plan.preview_token,
        acknowledge_unknown_positions: acknowledged });
      if (!result || result.error) {
        setError(result?.error || 'Could not save account changes');
        setReview(null);
      } else {
        await onChanged?.(result);
        onClose();
      }
    } catch (e) {
      setError('Could not save account changes. Review again before retrying.'); setReview(null);
    } finally { setBusy(false); }
  };
  const positions = review?.plan.eligible.flatMap((i) => i.positions) || [];
  const warnings = [...new Map(positions.filter((p) => p.quantity !== 0).map((p) => [p.symbol, p])).values()];
  const unknown = positions.some((p) => p.quantity === null);
  const lastCopies = review?.plan.eligible.filter((i) => candidates.find((c) => key(c) === key(i))?.accounts.length === 1) || [];
  const buttonClass = 'rounded-lg border border-olive-200 px-3 py-2 text-xs font-bold disabled:opacity-40';

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <section role="dialog" aria-modal="true" aria-label="Account instruments"
        className="max-h-[90vh] w-full max-w-lg overflow-y-auto rounded-2xl bg-white p-5 shadow-xl">
        <div className="mb-4 flex items-center justify-between">
          <h2 className={`text-sm font-bold ${theme.textPrimary}`}>Account instruments</h2>
          <button onClick={onClose} disabled={busy} aria-label="Close account instruments">×</button>
        </div>
        {error && <p role="alert" className="mb-3 text-xs text-red-600">{error}</p>}
        {!review ? (
          <>
            <div className="mb-3 flex gap-2">
              {['move', 'detach'].map((value) => (
                <button key={value} disabled={busy} className={`${buttonClass} ${operation === value ? 'bg-olive-100' : ''}`}
                  aria-pressed={operation === value} onClick={() => setOperation(value)}>
                  {value === 'move' ? 'Move' : 'Detach'}
                </button>
              ))}
            </div>
            <label className="mb-3 block text-xs">Source account
              <select aria-label="Source account" value={source} disabled={busy}
                className="mt-1 block w-full rounded-lg border p-2" onChange={(e) => changeSource(e.target.value)}>
                {accountNames.map((a) => <option key={a} value={a}>{a}</option>)}
              </select>
            </label>
            {operation === 'move' && <label className="mb-3 block text-xs">Destination account
              <select aria-label="Destination account" value={target} disabled={busy}
                className="mt-1 block w-full rounded-lg border p-2" onChange={(e) => setTarget(e.target.value)}>
                {accountNames.filter((a) => a !== source).map((a) => <option key={a} value={a}>{a}</option>)}
              </select>
            </label>}
            <p className={`mb-3 text-xs ${theme.textMuted}`}>
              {operation === 'move'
                ? 'Settings move with the instrument. Symbols already on the destination stay on the source. MONITOR keeps settings and disables trading.'
                : 'Remove only this account’s copy. Other accounts keep their instruments and settings.'}
            </p>
            <div className="mb-2 flex gap-3">
              <button className="text-xs font-bold" disabled={busy} onClick={() => setSelected(candidates.map(key))}>Select all</button>
              <button className="text-xs" disabled={busy} onClick={() => setSelected([])}>Select none</button>
            </div>
            <div className="mb-4 max-h-52 space-y-1 overflow-auto">
              {candidates.map((i) => <label key={key(i)} className="flex items-center gap-2 rounded-lg bg-olive-50 p-2 text-xs">
                <input type="checkbox" aria-label={`Select ${i.symbol} ${i.sectype || ''}`} disabled={busy}
                  checked={selected.includes(key(i))} onChange={() => setSelected((prev) => prev.includes(key(i))
                    ? prev.filter((k) => k !== key(i)) : [...prev, key(i)])} />
                {i.symbol} <span className={theme.textMuted}>{i.sectype}</span>
              </label>)}
              {candidates.length === 0 && <p className="text-xs">No instruments on this account.</p>}
            </div>
            <button className={`${buttonClass} bg-olive-100`} disabled={busy || selected.length === 0 || (operation === 'move' && !target)}
              onClick={loadPreview}>{busy ? 'Checking positions…' : operation === 'move' ? 'Review transfer' : 'Review detach'}</button>
          </>
        ) : (
          <>
            <p className="mb-3 text-xs font-semibold">{source}{operation === 'move' ? ` → ${target}` : ': detach'}</p>
            <ul className="mb-3 space-y-1 text-xs">
              {review.plan.eligible.map((i) => <li key={key(i)}>{i.symbol} · {i.sectype}</li>)}
            </ul>
            {review.plan.skipped.length > 0 && <div className="mb-3 text-xs">
              <p className="font-semibold">Already on destination; stays on source:</p>
              <p>{review.plan.skipped.map((i) => `${i.symbol} ${i.sectype}`).join(', ')}</p>
            </div>}
            <div className="mb-3 rounded-lg bg-amber-50 p-3 text-xs text-amber-900">
              <p className="mb-1 font-semibold">Positions stay at the broker on {source}. No orders are sent.</p>
              {warnings.map((p) => <p key={p.symbol}>{p.symbol}: {p.quantity === null ? 'unknown — broker unavailable' : p.quantity}</p>)}
              {warnings.length === 0 && <p>No open positions reported for these instruments.</p>}
              {unknown && <label className="mt-2 flex items-start gap-2">
                <input type="checkbox" aria-label="Acknowledge unknown positions" checked={acknowledged}
                  onChange={(e) => setAcknowledged(e.target.checked)} disabled={busy} />
                I understand that positions could not be checked and will manage them on the original account.
              </label>}
            </div>
            {operation === 'detach' && lastCopies.length > 0 && <p className="mb-3 text-xs text-red-600">
              Removing the last account copy removes these instruments from the library: {lastCopies.map((i) => i.symbol).join(', ')}.
            </p>}
            <div className="flex gap-2">
              <button className={buttonClass} disabled={busy} onClick={() => setReview(null)}>Back</button>
              <button className={`${buttonClass} ${operation === 'detach' ? 'bg-red-50 text-red-600' : 'bg-olive-100'}`}
                disabled={busy || review.plan.eligible.length === 0 || (unknown && !acknowledged)} onClick={confirm}>
                {busy ? 'Saving…' : operation === 'move' ? 'Confirm transfer' : 'Confirm detach'}
              </button>
            </div>
          </>
        )}
      </section>
    </div>
  );
}
